import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import AIInsightCard from "../components/AIInsightCard";
import RetentionPanel from "../components/RetentionPanel";
import { fetchEmployees, fetchAttritionRisks, fetchCriticalRoles, askAi } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, AlertTriangle, ShieldAlert, TrendingDown, Sparkles, Inbox, Compass, TrendingUp, ShieldCheck, ArrowRight } from "lucide-react";
import ManagerMentorPanel from "../components/ManagerMentorPanel";
import SuggestTrainingDrawer from "../components/SuggestTrainingDrawer";
import SuggestionsOutboxPanel from "../components/SuggestionsOutboxPanel";
import ProactiveSuggestionsWidget from "../components/ProactiveSuggestionsWidget";
import NextActionsCard from "../components/NextActionsCard";
import { RiskExplainerDrawer } from "../components/ExplainerDrawers";
// Faz 3 kariyer widgetları
import CareerTreeView from "../components/CareerTreeView";
import CareerRadar from "../components/CareerRadar";
import CareerStepTimeline from "../components/CareerStepTimeline";
import TargetRoleBanner from "../components/TargetRoleBanner";
import KudosPointsCard from "../components/KudosPointsCard";
import RewardsCatalog from "../components/RewardsCatalog";
import RecognitionModule from "../components/RecognitionModule";
// Faz 4 YGG + Mentor
import YggManagerEvalForm from "../components/YggManagerEvalForm";
import YggDirectorPanel from "../components/YggDirectorPanel";
import { listYggCycles } from "../lib/api";

export default function ManagerDashboard() {
  const persona = JSON.parse(sessionStorage.getItem("plena_persona") || "null");
  const nav = useNavigate();
  const loc = useLocation();
  const [employees, setEmployees] = useState([]);
  const [attrition, setAttrition] = useState(null);
  const [critical, setCritical] = useState([]);
  const [aiTeamInsight, setAiTeamInsight] = useState(null);
  const [aiTeamLoading, setAiTeamLoading] = useState(true);

  const [suggestEmp, setSuggestEmp] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [mgrRewardsOpen, setMgrRewardsOpen] = useState(false);
  const [mgrTargetRefresh, setMgrTargetRefresh] = useState(0);
  const activeTab = useMemo(() => {
    if (loc.pathname.includes("/attrition")) return "attrition";
    if (loc.pathname.includes("/succession")) return "succession";
    if (loc.pathname.includes("/mentor") && !loc.pathname.includes("my-")) return "mentor";
    if (loc.pathname.includes("/ygg-team")) return "ygg-team";
    if (loc.pathname.includes("/director-panel")) return "director";
    if (loc.pathname.includes("/my-suggestions")) return "outbox";
    if (loc.pathname.includes("/my-career-tree")) return "my-career-tree";
    if (loc.pathname.includes("/my-recognitions")) return "my-recognitions";
    if (loc.pathname.includes("/my-career")) return "my-career";
    return "overview";
  }, [loc.pathname]);

  useEffect(() => {
    if (!persona) { nav("/"); return; }
    fetchEmployees().then((emps) => {
      // Manager sees their direct reports (yonetici field) + same department peers
      const me = emps.find((e) => e.id === persona.employee_id);
      const myDept = me?.department;
      const reports = emps.filter((e) => e.manager_id === persona.employee_id);
      // If no direct reports recorded, fall back to same department
      const team = reports.length > 0
        ? reports
        : emps.filter((e) => e.department === myDept && e.id !== persona.employee_id);
      setEmployees(team);
    });
    fetchAttritionRisks().then(setAttrition);
    fetchCriticalRoles().then(setCritical);
  }, [persona?.employee_id]);

  useEffect(() => {
    if (employees.length === 0) return;
    setAiTeamLoading(true);
    const ctx = `${persona.title} - takımım: ${employees.map((e) => `${e.name} (${e.title}, perf ${e.performance_rating}, bağlılık ${e.engagement_score})`).join("; ")}`;
    askAi(ctx, "Takımımın en kritik 2 yetenek riski nedir? Maks 3 cümle.").then((a) => {
      setAiTeamInsight(a);
      setAiTeamLoading(false);
    });
  }, [employees.length]);

  const retryTeamInsight = async () => {
    setAiTeamLoading(true);
    setAiTeamInsight(null);
    const ctx = `${persona.title} - takımım: ${employees.map((e) => `${e.name} (${e.title}, perf ${e.performance_rating}, bağlılık ${e.engagement_score})`).join("; ")}`;
    const a = await askAi(ctx, "Takımımın en kritik 2 yetenek riski nedir? Maks 3 cümle.");
    setAiTeamInsight(a);
    setAiTeamLoading(false);
  };

  if (!persona) return null;

  // Suggest training drawer
  const MGR_ID = persona.employee_id;

  // Derive manager's department from team or persona
  const myDept = employees[0]?.department || "";
  const teamIds = new Set(employees.map((e) => e.id));
  const myAttrition = (attrition?.risks || []).filter((r) => teamIds.has(r.employee_id) || r.department === myDept);
  const myCritical = critical.filter((c) => c.department === myDept);
  const topRisk = myAttrition[0];

  // Group hierarchy
  const TAB_TO_GROUP = { overview: "team", attrition: "team", succession: "team", mentor: "actions", outbox: "actions", "ygg-team": "actions", director: "actions", "my-career": "career", "my-career-tree": "career", "my-recognitions": "career" };
  const activeGroup = TAB_TO_GROUP[activeTab] || "team";

  return (
    <Layout persona={persona}>
      <div className="mb-8">
        <div className="label-eyebrow text-amber-600 mb-2">Yönetici Görünümü</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Takım Zekası</h1>
        <p className="text-base text-slate-600 mt-3 max-w-2xl leading-relaxed">
          Ekibinin yetkinlik dağılımını, ayrılma risklerini ve yedekleme planlarını burada izleyebilirsin.
        </p>
      </div>

      <Tabs
        value={activeGroup}
        onValueChange={(g) => {
          const firstTab = { team: "overview", actions: "mentor", career: "my-career" }[g];
          const map = { overview: "/manager", attrition: "/manager/attrition", succession: "/manager/succession", mentor: "/manager/mentor", outbox: "/manager/my-suggestions", "my-career": "/manager/my-career", "my-career-tree": "/manager/my-career-tree", "my-recognitions": "/manager/my-recognitions" };
          nav(map[firstTab]);
        }}
      >
        <TabsList className="bg-white border border-slate-200 mb-6 p-1.5 h-auto flex-wrap">
          <TabsTrigger value="team" data-testid="grp-mgr-team" className="text-base px-5 py-2.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
            <Users className="w-4 h-4 mr-1.5" /> Ekibim
          </TabsTrigger>
          <TabsTrigger value="actions" data-testid="grp-mgr-actions" className="text-base px-5 py-2.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-800 data-[state=active]:shadow-sm">
            <Inbox className="w-4 h-4 mr-1.5" /> Aksiyonlar
          </TabsTrigger>
          <TabsTrigger value="career" data-testid="grp-mgr-career" className="text-base px-5 py-2.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
            <Compass className="w-4 h-4 mr-1.5" /> Kariyerim
          </TabsTrigger>
        </TabsList>

        {/* ============== GROUP: EKİBİM ============== */}
        <TabsContent value="team">
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              const map = { overview: "/manager", attrition: "/manager/attrition", succession: "/manager/succession" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto">
              <TabsTrigger value="overview" data-testid="mgr-tab-overview" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Takım Görünümü</TabsTrigger>
              <TabsTrigger value="attrition" data-testid="mgr-tab-attrition" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Ayrılma Riski</TabsTrigger>
              <TabsTrigger value="succession" data-testid="mgr-tab-succession" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Yedekleme</TabsTrigger>
            </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <Stat label="Takım Büyüklüğü" value={employees.length} icon={Users} accent="sky" />
            <Stat label="Yüksek Risk" value={myAttrition.filter((r) => r.risk_level === "high").length} icon={AlertTriangle} accent="rose" />
            <Stat label="Kritik Rol (bölüm)" value={myCritical.length} icon={ShieldAlert} accent="amber" />
            <Stat label="Takım Ort. Bağlılık" value={employees.length ? Math.round(employees.reduce((a, e) => a + (e.engagement_score || 0), 0) / employees.length) : "-"} icon={TrendingDown} accent="emerald" />
          </div>

          <ProactiveSuggestionsWidget suggesterId={MGR_ID} scope="team" />

          <NextActionsCard
            persona={persona}
            role="manager"
            highRiskCount={myAttrition.filter((r) => r.risk_level === "high").length}
            attritionTabPath="/manager/attrition"
            mentorTabPath="/manager/mentor"
            outboxTabPath="/manager/my-suggestions"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 card-surface p-6">
              <div className="label-eyebrow mb-4">Takım Üyeleri</div>
              <div className="space-y-2">
                {employees.map((e) => (
                  <TeamRow key={e.id} e={e} risk={myAttrition.find((r) => r.employee_id === e.id)} onSuggest={() => { setSuggestEmp(e); setSuggestOpen(true); }} />
                ))}
                {employees.length === 0 && <div className="text-sm text-slate-500">Takım üyesi yükleniyor...</div>}
              </div>
            </div>
            <AIInsightCard title="Takım Riski Yorumu" loading={aiTeamLoading} insight={aiTeamInsight} accent="amber" onRetry={retryTeamInsight} />
          </div>
        </TabsContent>

        <TabsContent value="attrition" className="space-y-6">
          {topRisk && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <h3 className="font-display text-xl font-semibold">Takım Risk Radarı — Öncelikli Çalışan</h3>
              </div>
              <RetentionPanel employeeId={topRisk.employee_id} />
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-2">Diğer Riskli Takım Üyeleri</div>
            <AttritionList items={myAttrition.slice(topRisk ? 1 : 0)} />
          </div>
        </TabsContent>

        <TabsContent value="succession" className="space-y-6">
          <SuccessionList items={myCritical} />
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: AKSIYONLAR ============== */}
        <TabsContent value="actions">
          <Tabs
            value={["outbox","ygg-team","director"].includes(activeTab) ? activeTab : "mentor"}
            onValueChange={(v) => {
              const map = { mentor: "/manager/mentor", outbox: "/manager/my-suggestions", "ygg-team": "/manager/ygg-team", director: "/manager/director-panel" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto flex-wrap">
              <TabsTrigger value="mentor" data-testid="mgr-tab-mentor" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
                <Inbox className="w-3.5 h-3.5 mr-1.5" /> Mentor Talepleri
              </TabsTrigger>
              <TabsTrigger value="ygg-team" data-testid="mgr-tab-ygg-team" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> YGG Ekip Değerlendirmesi
              </TabsTrigger>
              <TabsTrigger value="director" data-testid="mgr-tab-director" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-amber-800 data-[state=active]:shadow-sm">
                <Users className="w-3.5 h-3.5 mr-1.5" /> Direktör Panosu
              </TabsTrigger>
              <TabsTrigger value="outbox" data-testid="mgr-tab-outbox" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-amber-800 data-[state=active]:shadow-sm">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Önerdiklerim
              </TabsTrigger>
            </TabsList>

        <TabsContent value="mentor" className="space-y-6">
          <ManagerMentorPanel managerId={persona?.employee_id} teamMembers={employees} />
        </TabsContent>

        <TabsContent value="ygg-team" className="space-y-6" data-testid="mgr-tabcontent-ygg-team">
          <YggTeamEvalSection managerId={MGR_ID} teamMembers={employees} />
        </TabsContent>

        <TabsContent value="director" className="space-y-6" data-testid="mgr-tabcontent-director">
          <YggDirectorPanel directorId={MGR_ID} />
        </TabsContent>

        <TabsContent value="outbox" className="space-y-6">
          <SuggestionsOutboxPanel suggesterId={MGR_ID} />
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: KARİYERİM (manager's own career) ============== */}
        <TabsContent value="career">
          <div className="mb-5">
            <TargetRoleBanner
              employeeId={MGR_ID}
              onChanged={() => setMgrTargetRefresh((k) => k + 1)}
              onSelectTarget={() => nav("/manager/my-career-tree")}
            />
          </div>
          <Tabs
            value={["my-career","my-career-tree","my-recognitions"].includes(activeTab) ? activeTab : "my-career"}
            onValueChange={(v) => {
              const map = { "my-career": "/manager/my-career", "my-career-tree": "/manager/my-career-tree", "my-recognitions": "/manager/my-recognitions" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto flex-wrap">
              <TabsTrigger value="my-career" data-testid="mgr-tab-my-career" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Kariyer Basamağım
              </TabsTrigger>
              <TabsTrigger value="my-career-tree" data-testid="mgr-tab-my-career-tree" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <Compass className="w-3.5 h-3.5 mr-1.5" /> Kariyer Ağacı
              </TabsTrigger>
              <TabsTrigger value="my-recognitions" data-testid="mgr-tab-my-recognitions" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Takdirlerim
              </TabsTrigger>
            </TabsList>

            <TabsContent value="my-career" className="space-y-6">
              <CareerStepTimeline employeeId={MGR_ID} />
              <CareerRadar employeeId={MGR_ID} />
            </TabsContent>
            <TabsContent value="my-career-tree" className="space-y-6">
              <CareerTreeView employeeId={MGR_ID} onTargetChange={() => setMgrTargetRefresh((k) => k + 1)} />
            </TabsContent>
            <TabsContent value="my-recognitions" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2">
                  <RecognitionModule currentEmployeeId={MGR_ID} mode="employee" />
                </div>
                <div>
                  <KudosPointsCard employeeId={MGR_ID} onOpenCatalog={() => setMgrRewardsOpen(true)} />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          <RewardsCatalog employeeId={MGR_ID} open={mgrRewardsOpen} onOpenChange={setMgrRewardsOpen} />
        </TabsContent>
      </Tabs>
      <SuggestTrainingDrawer open={suggestOpen} onOpenChange={setSuggestOpen} employee={suggestEmp} suggesterId={MGR_ID} />
    </Layout>
  );
}

const Stat = ({ label, value, icon: Icon, accent }) => {
  const accents = {
    sky: "text-sky-600 border-sky-500/20",
    amber: "text-amber-600 border-amber-500/20",
    rose: "text-rose-600 border-rose-500/20",
    emerald: "text-emerald-600 border-emerald-500/20",
  };
  return (
    <div className={`card-surface p-6 ${accents[accent]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow">{label}</span>
        <Icon className="w-4 h-4" strokeWidth={1.7} />
      </div>
      <div className="font-display text-4xl font-semibold">{value}</div>
    </div>
  );
};

const TeamRow = ({ e, risk, onSuggest }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition" data-testid={`team-row-${e.id}`}>
    <Avatar className="w-10 h-10">
      <AvatarImage src={e.avatar} />
      <AvatarFallback>{e.name?.charAt(0)}</AvatarFallback>
    </Avatar>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium truncate">{e.name}</div>
      <div className="text-[0.7rem] text-slate-500 truncate">{e.title} · {e.tenure_years}y</div>
    </div>
    <div className="hidden sm:flex items-center gap-4 text-xs">
      <div className="text-right">
        <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">Perf</div>
        <div className="font-mono text-slate-800">{e.performance_rating}</div>
      </div>
      <div className="text-right">
        <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">Bağlılık</div>
        <div className="font-mono text-slate-800">{e.engagement_score}</div>
      </div>
    </div>
    {risk && <RiskBadge level={risk.risk_level} score={risk.risk_score} />}
    {onSuggest && (
      <button
        onClick={onSuggest}
        data-testid={`mgr-suggest-${e.id}`}
        className="ml-1 text-[0.7rem] px-2.5 py-1 rounded-md border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 inline-flex items-center gap-1 flex-shrink-0"
      >
        <Sparkles className="w-3 h-3" /> Gelişim öner
      </button>
    )}
  </div>
);

export const RiskBadge = ({ level, score }) => {
  const map = {
    high: { cls: "border-rose-500/40 text-rose-700 bg-rose-500/10", label: "Yüksek" },
    medium: { cls: "border-amber-500/40 text-amber-700 bg-amber-500/10", label: "Orta" },
    low: { cls: "border-emerald-500/40 text-emerald-700 bg-emerald-500/10", label: "Düşük" },
  };
  const m = map[level] || map.low;
  return (
    <Badge variant="outline" className={`${m.cls} font-mono`} data-testid={`risk-${level}`}>
      {m.label} {score ? `· ${score}` : ""}
    </Badge>
  );
};

export const AttritionList = ({ items }) => {
  const [explainRisk, setExplainRisk] = useState(null);
  return (
    <div className="space-y-3">
      {items.map((r) => (
        <button
          type="button"
          key={r.employee_id}
          onClick={() => setExplainRisk(r)}
          className="w-full text-left card-surface p-6 hover:ring-2 hover:ring-rose-200 transition"
          data-testid={`attrition-row-${r.employee_id}`}
        >
          <div className="flex items-center gap-4 mb-4">
            <Avatar className="w-11 h-11">
              <AvatarImage src={r.avatar} />
              <AvatarFallback>{r.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{r.name}</div>
              <div className="text-[0.7rem] text-slate-500">{r.title} · {r.department}</div>
            </div>
            <RiskBadge level={r.risk_level} score={r.risk_score} />
          </div>
          {r.signals?.length > 0 && (
            <div className="space-y-2">
              <div className="label-eyebrow flex items-center justify-between">
                <span>Sinyaller</span>
                <span className="text-[0.65rem] text-sky-700">Tıkla, neden hesaplandı? →</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {r.signals.map((s, i) => (
                  <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${
                    s.severity === "high" ? "border-rose-500/30 bg-rose-500/10 text-rose-800" : "border-amber-500/30 bg-amber-500/10 text-amber-700"
                  }`}>
                    <span className="font-medium">{s.label}</span>
                    <span className="font-mono text-[0.7rem] opacity-80">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </button>
      ))}
      {items.length === 0 && (
        <div className="card-surface p-10 text-center text-slate-500 text-sm">Yüksek/Orta riskli çalışan tespit edilmedi.</div>
      )}
      <RiskExplainerDrawer
        open={!!explainRisk}
        onClose={() => setExplainRisk(null)}
        employee={explainRisk ? { name: explainRisk.name } : null}
        risk={explainRisk}
      />
    </div>
  );
};

export const SuccessionList = ({ items, onSelect }) => (
  <div className="space-y-5">
    {items.map((c) => (
      <div key={c.crit_id || c.role_id} className="card-surface p-6" data-testid={`succession-role-${c.role_id}`}>
        <div className="flex items-start justify-between mb-5 gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              <h3 className="font-display text-2xl font-semibold">{c.role_title}</h3>
              <Badge variant="outline" className="border-rose-500/40 text-rose-700 bg-rose-500/10 text-[0.65rem]">Kritik</Badge>
            </div>
            <div className="text-xs text-slate-500">{c.department} · {c.career_band}</div>
            {c.status_label && (
              <div className="mt-2">
                <span className="text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                  {c.status_label}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <RiskBadge level={c.risk_level} score={c.risk_score} />
            {onSelect && c.crit_id && (
              <button
                onClick={() => onSelect(c.crit_id)}
                data-testid={`succession-open-${c.crit_id}`}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white transition"
              >
                Detay
              </button>
            )}
          </div>
        </div>

        {c.rationale && (
          <p className="text-xs text-slate-600 mb-4 leading-relaxed border-l-2 border-amber-300 pl-3">{c.rationale}</p>
        )}

        {c.incumbents?.length > 0 && (
          <div className="mb-4">
            <div className="label-eyebrow mb-2">Mevcut Pozisyon Sahibi</div>
            <div className="flex flex-wrap gap-2">
              {c.incumbents.map((i) => (
                <div key={i.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-300 bg-slate-50/80">
                  <Avatar className="w-6 h-6"><AvatarImage src={i.avatar} /><AvatarFallback>{i.name?.charAt(0)}</AvatarFallback></Avatar>
                  <span className="text-xs text-slate-800">{i.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="label-eyebrow mb-2">Yedekleme Adayları ({c.candidates.length})</div>
        <div className="space-y-2">
          {c.candidates.slice(0, 4).map((cand) => (
            <div key={cand.employee_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <Avatar className="w-9 h-9"><AvatarImage src={cand.avatar} /><AvatarFallback>{cand.name?.charAt(0)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{cand.name}</div>
                <div className="text-[0.7rem] text-slate-500 truncate">Açıklar: {cand.key_gaps?.join(", ") || "yok"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-semibold text-sky-600">%{cand.readiness_score}</div>
                <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">{cand.time_to_ready_months} ay</div>
              </div>
            </div>
          ))}
          {c.candidates.length === 0 && <div className="text-xs text-slate-500">Henüz uygun aday tespit edilmedi.</div>}
        </div>
      </div>
    ))}
    {items.length === 0 && (
      <div className="card-surface p-10 text-center text-slate-500 text-sm">Bu görünüm için kritik rol bulunamadı.</div>
    )}
  </div>
);

/**
 * YggTeamEvalSection — Manager sees active YGG cycle and can fill evaluation for each direct report.
 * Each row shows stage status so manager knows: waiting on survey / bana düştü / ilerledi.
 */
function YggTeamEvalSection({ managerId, teamMembers }) {
  const [activeCycle, setActiveCycle] = useState(null);
  const [cycleDetail, setCycleDetail] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    listYggCycles().then(async (d) => {
      const active = (d.items || []).find((c) => c.status === "active");
      setActiveCycle(active || null);
      if (active) {
        const { getYggCycle } = await import("../lib/api");
        const detail = await getYggCycle(active.id);
        setCycleDetail(detail);
      }
    }).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const directReports = (teamMembers || []).filter((e) => e.manager_id === managerId);
  const participantMap = (cycleDetail?.participants || []).reduce((acc, p) => { acc[p.employee_id] = p; return acc; }, {});

  const stageMeta = (p) => {
    const s = p?.stage_status || {};
    if (s.calibration === "completed") return { label: "Kalibre edildi", color: "emerald", waiting: false };
    if (s.director_review === "completed") return { label: "Direktörde kalibrasyon bekliyor", color: "amber", waiting: false };
    if (s.manager_eval === "completed") return { label: "Direktör onayında", color: "indigo", waiting: false };
    if (s.survey === "completed") return { label: "Sıra sende — değerlendirme yap", color: "rose", waiting: true };
    return { label: "Çalışan anketi bekliyor", color: "slate", waiting: false };
  };

  if (loading) return <div className="card-surface p-6 text-sm text-slate-400">Yükleniyor…</div>;
  if (!activeCycle) return (
    <div className="card-surface p-6" data-testid="ygg-team-no-active">
      <div className="label-eyebrow mb-1">Aktif YGG Yok</div>
      <p className="text-sm text-slate-600">Ekibin için değerlendirme yapabilmen için İK&apos;nın bir YGG döngüsü başlatması gerekiyor.</p>
    </div>
  );

  const pendingForMe = directReports.filter((e) => stageMeta(participantMap[e.id]).waiting).length;

  return (
    <div className="space-y-4" data-testid="ygg-team-eval-section">
      <div className={`rounded-xl border p-4 flex items-center gap-3 ${pendingForMe > 0 ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"}`}>
        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold ${pendingForMe > 0 ? "bg-rose-500" : "bg-slate-400"}`}>
          {pendingForMe > 0 ? pendingForMe : "0"}
        </div>
        <div className="flex-1">
          <div className="label-eyebrow">Aktif YGG · {activeCycle.name}</div>
          <div className="text-sm font-semibold text-slate-800">
            {pendingForMe > 0 ? `Sana düşen ${pendingForMe} kişi var — anketleri tamamlanmış, değerlendirmen bekleniyor.` : "Şu an sıran değil — çalışanların anketi doldurmasını bekleyebilir veya direktör/İK aşamasındasın."}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Akış: Çalışan Anketi → <span className="font-semibold">Yönetici (sen)</span> → Direktör → İK Kalibrasyon</div>
        </div>
      </div>

      {selectedEmp ? (
        <div>
          <button onClick={() => { setSelectedEmp(null); reload(); }} className="mb-3 text-sm text-sky-700 hover:underline">← Geri (ekip listesi)</button>
          <YggManagerEvalForm cycleId={activeCycle.id} employeeId={selectedEmp.id} employeeName={selectedEmp.name} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[...directReports].sort((a, b) => (stageMeta(participantMap[b.id]).waiting ? 1 : 0) - (stageMeta(participantMap[a.id]).waiting ? 1 : 0)).map((e) => {
            const p = participantMap[e.id];
            const meta = stageMeta(p);
            const cmap = { emerald: "bg-emerald-100 text-emerald-800", amber: "bg-amber-100 text-amber-800", indigo: "bg-indigo-100 text-indigo-800", rose: "bg-rose-100 text-rose-800", slate: "bg-slate-100 text-slate-600" };
            return (
              <div key={e.id} className={`card-surface p-3 hover:shadow-md transition cursor-pointer border ${meta.waiting ? "border-rose-200" : "border-slate-100"}`} onClick={() => setSelectedEmp(e)} data-testid={`ygg-team-emp-${e.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2.5">
                    {e.avatar ? <img src={e.avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-slate-200" />}
                    <div>
                      <div className="font-medium text-sm">{e.name}</div>
                      <div className="text-xs text-slate-500">{e.role_title || e.title}</div>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${cmap[meta.color]}`}>{meta.label}</span>
                  {e.performance_rating != null && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-sky-50 text-sky-700">Perf {e.performance_rating}</span>}
                  {e.engagement_score != null && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-rose-50 text-rose-700">Bağlılık %{e.engagement_score}</span>}
                </div>
              </div>
            );
          })}
          {directReports.length === 0 && (
            <div className="col-span-2 text-sm text-slate-500 italic">Doğrudan raporun görünmüyor.</div>
          )}
        </div>
      )}
    </div>
  );
}

